import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button,
  Alert, CircularProgress, Divider, InputAdornment, Tooltip, Chip, useTheme
} from '@mui/material';
import { Save, TrendingUp, Info, ElectricBolt, CurrencyExchange, BarChart } from '@mui/icons-material';
import { MarketParams } from '../../types';
import { getMarketParams, updateMarketParams } from '../../api/marketParams';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

const HAMMADDE = [
  { label: 'PVC İzolasyon', key: 'pvcIzolasyonFiyat', color: '#e3f2fd', group: 'İzolasyon' },
  { label: 'XLPE İzolasyon', key: 'xlpeIzolasyonFiyat', color: '#e3f2fd', group: 'İzolasyon' },
  { label: 'PE İzolasyon', key: 'peIzolasyonFiyat', color: '#e3f2fd', group: 'İzolasyon' },
  { label: 'PVC Kılıf', key: 'pvcKilafFiyat', color: '#fce4ec', group: 'Kılıf' },
  { label: 'HFFR Kılıf', key: 'hffrKilafFiyat', color: '#fce4ec', group: 'Kılıf' },
  { label: 'PE Dolgu', key: 'peDolguFiyat', color: '#f3e5f5', group: 'Dolgu' },
  { label: 'PVC Dolgu', key: 'pvcDolguFiyat', color: '#f3e5f5', group: 'Dolgu' },
  { label: 'HFFR Dolgu', key: 'hffrDolguFiyat', color: '#f3e5f5', group: 'Dolgu' },
  { label: 'HDPE', key: 'hdpeFiyat', color: '#e8f5e9', group: 'Diğer' },
];

const groups = ['İzolasyon', 'Kılıf', 'Dolgu', 'Diğer'];

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; accent: string; children: React.ReactNode }> =
  ({ title, icon, accent, children }) => (
    <Paper elevation={0} sx={{
      p: 2.5, flex: 1, minWidth: 260,
      border: '1px solid', borderColor: 'divider',
      borderTop: `3px solid ${accent}`,
      borderRadius: '10px', position: 'relative'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
        <Box sx={{
          width: 36, height: 36, bgcolor: accent + '15', borderRadius: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent
        }}>
          {icon}
        </Box>
        <Typography sx={{
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: accent
        }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );

const MarketParamsPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [params, setParams] = useState<Partial<MarketParams>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getMarketParams()
      .then(setParams)
      .catch(() => setError('Parametreler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await updateMarketParams(params);
      setSuccess('Parametreler güncellendi — tüm ürün maliyetleri otomatik güncellendi');
      setTimeout(() => setSuccess(''), 4000);
    } catch { setError('Güncelleme sırasında hata oluştu'); }
    finally { setSaving(false); }
  };

  const vergiLabel = (kad?: number) => kad === 0 ? '%0 Vergisiz' : kad === 2 ? '%7.5 Vergili' : '-';
  const alasim = ((params.lmeAl || 0) + (params.alPrim || 0) + 650).toFixed(2);
  const eurUsd = params.eurTry && params.usdTry ? (params.eurTry / params.usdTry).toFixed(4) : '-';

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ color: BRAND, fontSize: 22 }} />
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Maliyet Parametreleri
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            Kaydet'e basınca tüm kablo maliyetleri otomatik güncellenir
          </Typography>
        </Box>
        <Button variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                onClick={handleSave} disabled={saving}
                sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                  px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                  '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
          Kaydet & Güncelle
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      {/* ── 3 Kart ── */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 3,
                 '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>

        {/* Alüminyum */}
        <SectionCard title="Alüminyum" icon={<ElectricBolt sx={{ fontSize: 18 }} />} accent={BRAND}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="LME AL" type="number" size="small" value={params.lmeAl || ''}
                       onChange={(e) => setParams({ ...params, lmeAl: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">$/ton</InputAdornment> }} fullWidth />
            <TextField label="AL Prim" type="number" size="small" value={params.alPrim || ''}
                       onChange={(e) => setParams({ ...params, alPrim: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">$/ton</InputAdornment> }} fullWidth />
            <Divider />
            <Box sx={{ bgcolor: '#eff6ff', p: 1.75, borderRadius: '8px', border: '1px solid #dbeafe' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT2,
                                 letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                Alaşım = LME + Prim + 650
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: BRAND, fontFamily: 'monospace', lineHeight: 1.1 }}>
                {alasim}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: TEXT2 }}>$/ton</Typography>
            </Box>
          </Box>
        </SectionCard>

        {/* Döviz */}
        <SectionCard title="Döviz Kurları" icon={<CurrencyExchange sx={{ fontSize: 18 }} />} accent="#16A34A">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="USD/TRY" type="number" size="small" value={params.usdTry || ''}
                       onChange={(e) => setParams({ ...params, usdTry: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }} fullWidth />
            <TextField label="EUR/TRY" type="number" size="small" value={params.eurTry || ''}
                       onChange={(e) => setParams({ ...params, eurTry: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }} fullWidth />
            <Divider />
            <Box sx={{ bgcolor: '#f0fdf4', p: 1.75, borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT2,
                                 letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                EUR/USD
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#16A34A', fontFamily: 'monospace', lineHeight: 1.1 }}>
                {eurUsd}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: TEXT2 }}>hesaplanmış</Typography>
            </Box>
          </Box>
        </SectionCard>

        {/* Ek Maliyetler */}
        <SectionCard title="Ek Maliyetler" icon={<BarChart sx={{ fontSize: 18 }} />} accent="#B45309">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Finans" type="number" size="small" value={params.finans || ''}
                       onChange={(e) => setParams({ ...params, finans: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} fullWidth />
            <TextField label="Nakliye" type="number" size="small" value={params.nakliye || ''}
                       onChange={(e) => setParams({ ...params, nakliye: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} fullWidth />
            <TextField label="Diğer" type="number" size="small" value={params.diger || ''}
                       onChange={(e) => setParams({ ...params, diger: parseFloat(e.target.value) })}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} fullWidth />
            <TextField select label="Vergi Kademesi" size="small" value={params.vergiKad ?? ''}
                       onChange={(e) => setParams({ ...params, vergiKad: parseInt(e.target.value) })}
                       fullWidth SelectProps={{ native: true }}>
              <option value={0}>Vergisiz — %0</option>
              <option value={2}>Vergili — %7.5</option>
            </TextField>
            <Box sx={{ bgcolor: '#fff7ed', p: 1.75, borderRadius: '8px', border: '1px solid #fed7aa' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT2,
                                 letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                Aktif Vergi
              </Typography>
              <Chip label={vergiLabel(params.vergiKad)} size="small"
                    sx={{ height: 22, bgcolor: '#B45309', color: 'white', fontWeight: 700,
                      fontSize: '11px', borderRadius: '6px' }} />
            </Box>
          </Box>
        </SectionCard>
      </Box>

      {/* ── Hammadde Fiyatları ── */}
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
          <Box sx={{
            width: 36, height: 36, bgcolor: '#faf5ff', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Typography sx={{ fontSize: 18 }}>🧱</Typography>
          </Box>
          <Typography sx={{
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: '#7C3AED'
          }}>
            Hammadde Fiyatları
          </Typography>
          <Tooltip title="Kaydet'e basınca tüm kablo ailelerinin maliyetleri otomatik güncellenir">
            <Info sx={{ fontSize: 16, color: TEXT2, ml: 0.5 }} />
          </Tooltip>
        </Box>

        {groups.map(group => {
          const items = HAMMADDE.filter(h => h.group === group);
          return (
              <Box key={group} sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '11px', fontWeight: 700, color: TEXT2,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  display: 'block', mb: 1.5
                }}>
                  {group}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {items.map(({ label, key, color }) => (
                      <Box key={key} sx={{
                        p: 2, bgcolor: color, borderRadius: '10px',
                        border: '1px solid', borderColor: 'divider',
                        minWidth: 180, flex: '1 1 180px', maxWidth: 240
                      }}>
                        <Typography sx={{
                          fontSize: '10px', fontWeight: 700, color: '#475569',
                          letterSpacing: '0.05em', textTransform: 'uppercase',
                          display: 'block', mb: 1
                        }}>
                          {label}
                        </Typography>
                        <TextField type="number" size="small" fullWidth
                                   value={(params as any)[key] ?? ''}
                                   onChange={(e) => setParams({ ...params, [key]: parseFloat(e.target.value) })}
                                   InputProps={{ endAdornment: <InputAdornment position="end">$/t</InputAdornment> }}
                                   inputProps={{ step: 10, min: 0 }}
                                   sx={{ bgcolor: 'white',
                                         '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      </Box>
                  ))}
                </Box>
                {group !== 'Diğer' && <Divider sx={{ mt: 2.5 }} />}
              </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default MarketParamsPage;
